import React, { useId } from "react";
import styled from "styled-components";
import { Density, controlHeight, controlPadding, useDensity } from "./base/density";
import { bodyType, captionType, colorTransition, iconSlot } from "./base/mixins";

export interface SelectOption {
	value: string;
	label: string;
	disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size"> {
	options: SelectOption[];
	placeholder?: string;
	helperText?: React.ReactNode;
	error?: boolean;
	density?: Density;
}

/** §8: identical to Input, plus a 24px chevron 16px from the right edge. */
const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
	({ options, placeholder, helperText, error = false, density, className, id, value, ...rest }, ref) => {
		const generated = useId();
		const selectId = id ?? generated;
		const helperId = helperText ? `${selectId}-helper` : undefined;
		const resolved = useDensity(density);

		return (
			<Field className={className}>
				<Shell $height={controlHeight(resolved)} $pad={controlPadding(resolved)} $error={error}>
					<select
						ref={ref}
						id={selectId}
						value={value}
						aria-invalid={error || undefined}
						aria-describedby={helperId}
						{...rest}
					>
						{placeholder ? (
							<option value="" disabled>
								{placeholder}
							</option>
						) : null}
						{options.map((option) => (
							<option key={option.value} value={option.value} disabled={option.disabled}>
								{option.label}
							</option>
						))}
					</select>
					<span className="hds-select__chevron" aria-hidden>
						<ChevronDown />
					</span>
				</Shell>
				{helperText ? (
					<Helper id={helperId} $error={error}>
						{helperText}
					</Helper>
				) : null}
			</Field>
		);
	},
);

Select.displayName = "Select";

/* Inline until Phase 4 lands the icon set; drawn on the same 24 / 1.8 grid. */
const ChevronDown = () => (
	<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
		<path d="M6 9l6 6 6-6" />
	</svg>
);

const Field = styled.div`
	display: flex;
	flex-direction: column;
	width: 100%;
`;

const Shell = styled.div<{ $height: string; $pad: string; $error: boolean }>`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	height: ${({ $height }) => $height};
	border: var(--border-default);
	border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-border)")};
	border-radius: var(--radius-md);
	background: var(--color-surface);
	box-shadow: none;
	${colorTransition}

	&:hover {
		border-color: ${({ $error }) =>
			$error ? "var(--color-danger)" : "var(--color-border-strong)"};
	}
	/* §9: a field shows focus on its own border, not with a ring around it.
	   Keyboard focus only — :focus-within also fired on a mouse click, leaving a
	   picker lit after the user had chosen and moved on. */
	&:has(:focus-visible) {
		border-color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--focus-border)")};
	}

	select:focus-visible {
		outline: none;
	}

	select {
		flex: 1;
		min-width: 0;
		height: 100%;
		/* Room for the chevron: 16px edge inset + 24px icon + 8px gap. */
		padding: 0 calc(${({ $pad }) => $pad} + var(--size-icon) + var(--space-2)) 0
			${({ $pad }) => $pad};
		border: none;
		outline: none;
		background: transparent;
		appearance: none;
		cursor: pointer;
		${bodyType}

		&:disabled {
			cursor: not-allowed;
			color: var(--color-text-disabled);
		}
		&:invalid {
			color: var(--color-text-secondary);
		}
	}

	.hds-select__chevron {
		${iconSlot}
		position: absolute;
		right: ${({ $pad }) => $pad};
		pointer-events: none;
		color: var(--color-text);
	}
`;

const Helper = styled.span<{ $error: boolean }>`
	margin-top: var(--space-1);
	${captionType}
	color: ${({ $error }) => ($error ? "var(--color-danger)" : "var(--color-text-secondary)")};
`;

export default Select;
